import type { PointerEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useId, useMemo, useState } from 'react'
import { useElementWidth } from '@/hooks/use-element-width'
import { niceDomain, scaleLinear } from '@/components/charts/scales'

export interface ScatterDatum {
    key: string
    x: number
    y: number
    label?: string
    href?: string
    highlight?: boolean
    labelled?: boolean
    size?: number
    detail?: ReactNode
}

interface ScatterPlotProps {
    points: ScatterDatum[]
    height?: number
    xLabel: string
    yLabel: string
    xFormat: (value: number) => string
    yFormat: (value: number) => string
    regression?: { slope: number, intercept: number } | null
    ariaLabel: string
    zeroLines?: boolean
    dimOthers?: boolean
}

const LEFT = 52
const RIGHT = 14
const TOP = 12
const BOTTOM = 40

const ScatterPlot = ({ points, height = 320, xLabel, yLabel, xFormat, yFormat, regression = null, ariaLabel, zeroLines = true, dimOthers = false }: ScatterPlotProps): ReactNode => {
    const router = useRouter()
    const clipId = `scatter-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
    const [ref, width] = useElementWidth<HTMLDivElement>(560)
    const [active, setActive] = useState<number | null>(null)
    const plotWidth = Math.max(80, width - LEFT - RIGHT)
    const plotHeight = height - TOP - BOTTOM

    const { xDomain, yDomain } = useMemo(() => {
        const xs = points.map(point => point.x)
        const ys = points.map(point => point.y)

        return {
            xDomain: niceDomain(Math.min(...xs, zeroLines ? 0 : Infinity), Math.max(...xs), 5, 0.06),
            yDomain: niceDomain(Math.min(...ys, zeroLines ? 0 : Infinity), Math.max(...ys, zeroLines ? 0 : -Infinity), 5, 0.08)
        }
    }, [points, zeroLines])

    const x = scaleLinear([xDomain.min, xDomain.max], [LEFT, LEFT + plotWidth])
    const y = scaleLinear([yDomain.min, yDomain.max], [TOP + plotHeight, TOP])
    const radius = (point: ScatterDatum): number => (point.size !== undefined ? Math.max(3, Math.min(10, point.size)) : 4)

    const handleMove = (event: PointerEvent<SVGRectElement>) => {
        const svg = event.currentTarget.ownerSVGElement
        if (!svg || !points.length) return

        const bounds = svg.getBoundingClientRect()
        const scale = width / Math.max(1, bounds.width)
        const px = (event.clientX - bounds.left) * scale
        const py = (event.clientY - bounds.top) * scale
        let best = -1
        let bestDistance = Infinity

        points.forEach((point, index) => {
            const dx = x(point.x) - px
            const dy = y(point.y) - py
            const distance = dx * dx + dy * dy

            if (distance < bestDistance) {
                bestDistance = distance
                best = index
            }
        })

        setActive(bestDistance <= 28 * 28 ? best : null)
    }

    const ordered = useMemo(() => points.map((point, index) => ({ point, index })).sort((a, b) => Number(Boolean(a.point.highlight)) - Number(Boolean(b.point.highlight))), [points])
    const activePoint = active !== null ? points[active] : null

    return (
        <div ref={ref} className='relative w-full' role='group' aria-label={ariaLabel}>
            <svg width='100%' height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='xMinYMin meet' aria-hidden='true' className='block overflow-visible'>
                {yDomain.ticks.map(tick => (
                    <g key={`y-${tick}`}>
                        <line x1={LEFT} x2={LEFT + plotWidth} y1={y(tick)} y2={y(tick)} stroke='var(--grid)' shapeRendering='crispEdges' />
                        <text x={LEFT - 8} y={y(tick)} dy='0.32em' textAnchor='end' fontSize={11} fill='var(--muted-foreground)'>{yFormat(tick)}</text>
                    </g>
                ))}

                {xDomain.ticks.map(tick => (
                    <g key={`x-${tick}`}>
                        <line x1={x(tick)} x2={x(tick)} y1={TOP} y2={TOP + plotHeight} stroke='var(--grid)' shapeRendering='crispEdges' />
                        <text x={x(tick)} y={TOP + plotHeight + 16} textAnchor='middle' fontSize={11} fill='var(--muted-foreground)'>{xFormat(tick)}</text>
                    </g>
                ))}

                {zeroLines && yDomain.min < 0 && yDomain.max > 0 && (
                    <line x1={LEFT} x2={LEFT + plotWidth} y1={y(0)} y2={y(0)} stroke='var(--axis)' strokeWidth={1} />
                )}

                {zeroLines && xDomain.min < 0 && xDomain.max > 0 && (
                    <line x1={x(0)} x2={x(0)} y1={TOP} y2={TOP + plotHeight} stroke='var(--axis)' strokeWidth={1} />
                )}

                {regression && (
                    <line
                        x1={x(xDomain.min)}
                        x2={x(xDomain.max)}
                        y1={y(regression.intercept + regression.slope * xDomain.min)}
                        y2={y(regression.intercept + regression.slope * xDomain.max)}
                        stroke='var(--series-2)'
                        strokeWidth={2}
                        clipPath={`url(#${clipId})`}
                    />
                )}

                <defs>
                    <clipPath id={clipId}>
                        <rect x={LEFT} y={TOP} width={plotWidth} height={plotHeight} />
                    </clipPath>
                </defs>

                {ordered.map(({ point, index }) => {
                    const highlighted = !dimOthers || point.highlight

                    return (
                        <circle
                            key={point.key}
                            cx={x(point.x)}
                            cy={y(point.y)}
                            r={active === index ? radius(point) + 1.5 : radius(point)}
                            fill={highlighted ? 'var(--series-1)' : 'var(--muted-foreground)'}
                            fillOpacity={highlighted ? 0.9 : 0.35}
                            stroke='var(--surface)'
                            strokeWidth={2}
                        />
                    )
                })}

                {points.map(point => (point.labelled && point.label ? (
                    <text key={`label-${point.key}`} x={x(point.x) + radius(point) + 4} y={y(point.y)} dy='0.32em' fontSize={10.5} fontWeight={600} fill='var(--foreground)' fontFamily='var(--font-mono)' pointerEvents='none'>
                        {point.label}
                    </text>
                ) : null))}

                <text x={LEFT + plotWidth / 2} y={height - 4} textAnchor='middle' fontSize={11} fill='var(--muted-foreground)'>{xLabel}</text>
                <text x={12} y={TOP + plotHeight / 2} textAnchor='middle' fontSize={11} fill='var(--muted-foreground)' transform={`rotate(-90 12 ${TOP + plotHeight / 2})`}>{yLabel}</text>

                <rect
                    x={LEFT}
                    y={TOP}
                    width={plotWidth}
                    height={plotHeight}
                    fill='transparent'
                    style={{ cursor: activePoint?.href ? 'pointer' : 'default' }}
                    onPointerMove={handleMove}
                    onPointerLeave={() => setActive(null)}
                    onClick={() => {
                        if (activePoint?.href) void router.push(activePoint.href)
                    }}
                />
            </svg>

            {activePoint && (
                <div
                    className='pointer-events-none absolute z-10 min-w-40 rounded-lg border border-border bg-popover/95 px-3 py-2 text-xs shadow-xl'
                    style={{
                        left: `${Math.min(72, Math.max(0, (x(activePoint.x) / width) * 100 + 2))}%`,
                        top: Math.max(0, y(activePoint.y) - 70)
                    }}
                >
                    {activePoint.detail ?? (
                        <>
                            <p className='font-mono font-semibold text-foreground'>{activePoint.label}</p>
                            <p className='mt-1 text-muted-foreground'>{xLabel}: <span className='tabular-nums text-foreground'>{xFormat(activePoint.x)}</span></p>
                            <p className='text-muted-foreground'>{yLabel}: <span className='tabular-nums text-foreground'>{yFormat(activePoint.y)}</span></p>
                        </>
                    )}

                    {activePoint.href && (
                        <p className='mt-1.5 text-[11px] text-muted-foreground'>Select the point to open the company</p>
                    )}
                </div>
            )}
        </div>
    )
}

export default ScatterPlot
